import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetAdminRecruitmentApplicationsQuery,
  useGetAdminRecruitmentPositionsQuery,
  useGetAdminRecruitmentStatsQuery,
} from "../../features/api/appApi";
import AdminSectionCard from "./AdminSectionCard";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.gubcpc.com";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "selected", label: "Selected" },
  { value: "rejected", label: "Rejected" },
];

const FIXED_INTERVIEW_DATE_LABEL = "15 Apr 2026";

const formatStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  const found = STATUS_OPTIONS.find((item) => item.value === normalized);
  if (found) return found.label;
  if (!normalized) return "Unknown";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const cx = (...classes) => classes.filter(Boolean).join(" ");

const formatInterviewSchedule = (timeValue) => {
  const normalizedTime = String(timeValue || "").trim();
  if (!normalizedTime) return "-";
  return `${FIXED_INTERVIEW_DATE_LABEL}, ${normalizedTime}`;
};

const formatSentAt = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString();
};

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-4 w-4"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

const FileIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-4 w-4"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" />
    <path d="M14 2v7h7" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-3.5 w-3.5"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M14 3h7v7" />
    <path d="M10 14L21 3" />
    <path d="M21 14v4a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h4" />
  </svg>
);

const PhotoCell = ({ rowId, fullName, accessToken }) => {
  const [photoSrc, setPhotoSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const loadPhoto = async () => {
      try {
        setLoading(true);
        setFailed(false);

        const response = await fetch(
          `${API_BASE_URL}/admin/recruitment/applications/${rowId}/files/photo`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to load photo");

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setPhotoSrc(objectUrl);
        }
      } catch (error) {
        if (isMounted) {
          setFailed(true);
          setPhotoSrc("/default-profile.png");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (rowId && accessToken) {
      loadPhoto();
    } else {
      setPhotoSrc("/default-profile.png");
      setFailed(true);
      setLoading(false);
    }

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rowId, accessToken]);

  return (
    <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
      {loading ? (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200" />
      ) : null}

      {!loading ? (
        <img
          src={photoSrc || "/default-profile.png"}
          alt={fullName || "Applicant"}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/default-profile.png";
          }}
        />
      ) : null}

      {failed && !loading ? (
        <div className="absolute bottom-0 right-0 rounded-tl-md bg-slate-800 px-1 py-0.5 text-[9px] font-semibold text-white">
          N/A
        </div>
      ) : null}
    </div>
  );
};

const statusStyles = {
  pending: "bg-amber-100 text-amber-700 ring-amber-200",
  shortlisted: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  selected: "bg-blue-100 text-blue-700 ring-blue-200",
  default: "bg-slate-100 text-slate-700 ring-slate-200",
};

const StatusBadge = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  const tone = statusStyles[normalized] || statusStyles.default;

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ring-1",
        tone
      )}
    >
      {formatStatus(status)}
    </span>
  );
};

const TabButton = ({ active, children, ...props }) => (
  <button
    {...props}
    className={cx(
      "cursor-pointer rounded-xl px-4 py-2 text-sm font-semibold transition",
      active
        ? "bg-slate-900 text-white shadow-sm"
        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
    )}
  >
    {children}
  </button>
);

const ActionButton = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={cx(
      "cursor-pointer rounded-lg px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
      className
    )}
  >
    {children}
  </button>
);

const StatsListCard = ({ title, items = [], emptyText }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <h4 className="text-sm font-bold text-slate-900">{title}</h4>
    {!items.length ? (
      <p className="mt-3 text-xs text-slate-500">{emptyText}</p>
    ) : (
      <div className="mt-3 max-h-72 overflow-y-auto pr-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-slate-100">
        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={`${title}-${item.label}-${index}`}
              className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2"
            >
              <span className="min-w-0 truncate text-xs font-medium text-slate-700">
                {item.label}
              </span>
              <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-semibold text-white">
                {item.count}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const PositionFilterDropdown = ({
  value,
  options = [],
  open,
  setOpen,
  onChange,
  onClear,
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpen]);

  return (
    <div ref={dropdownRef} className="relative w-full lg:max-w-sm">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-left text-sm text-slate-800 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      >
        <span className="truncate">{value || "All Positions"}</span>
        <span className="text-slate-500">{open ? "▲" : "▼"}</span>
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <button
            type="button"
            className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => {
              onClear();
              setOpen(false);
            }}
          >
            All Positions
          </button>
          <div className="max-h-64 overflow-y-auto py-1">
            {options.length ? (
              options.map((position) => (
                <button
                  key={position}
                  type="button"
                  className={cx(
                    "block w-full px-3 py-2 text-left text-sm hover:bg-slate-50",
                    value === position
                      ? "bg-blue-50 font-semibold text-blue-700"
                      : "text-slate-700"
                  )}
                  onClick={() => {
                    onChange(position);
                    setOpen(false);
                  }}
                >
                  {position}
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-xs text-slate-500">No positions found</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LinkChip = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex min-w-0 cursor-pointer items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
  >
    {children}
  </a>
);

const FileButton = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
  >
    {children}
  </button>
);

const InfoRow = ({ label, value, href }) => (
  <p className="text-sm text-slate-700 break-words">
    <span className="font-semibold text-slate-900">{label}:</span>{" "}
    {href && value ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 break-all text-blue-600 underline"
      >
        {value}
        <ExternalLinkIcon />
      </a>
    ) : (
      value || "-"
    )}
  </p>
);

const RowRemarksEditor = ({ value, onChange, onSave, saving }) => {
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  return (
    <div className="w-full max-w-[220px] rounded-xl border border-slate-200 bg-slate-50 p-2.5">
      <textarea
        rows={3}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        placeholder="Add remarks..."
        className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
      />
      <button
        onClick={() => onSave(draft)}
        disabled={saving}
        className="mt-2 cursor-pointer rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

const RowStatusEditor = ({ value, onSave, saving }) => {
  const current = String(value || "pending").toLowerCase();
  const tone = statusStyles[current.toLowerCase()] || statusStyles.default;

  return (
    <div className="flex w-full min-w-[150px] flex-col items-start gap-2">
      <StatusBadge status={current} />
      <select
        value={current}
        disabled={saving}
        onChange={(e) => onSave(e.target.value)}
        className={cx(
          "w-full cursor-pointer rounded-xl border bg-white px-3 py-2 text-xs font-semibold capitalize outline-none transition ring-1",
          tone,
          "focus:border-slate-400 focus:ring-slate-200"
        )}
      >
        {STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const RowInterviewEditor = ({ value, onChange, onSave, saving }) => {
  const [draft, setDraft] = useState(String(value || ""));

  useEffect(() => {
    setDraft(String(value || ""));
  }, [value]);

  return (
    <div className="w-full min-w-[180px] rounded-xl border border-slate-200 bg-slate-50 p-2.5">
      <p className="mb-1 text-[11px] font-semibold text-slate-600">{FIXED_INTERVIEW_DATE_LABEL}</p>
      <input
        type="time"
        value={draft}
        disabled={saving}
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
      />
      <button
        onClick={() => onSave(draft)}
        disabled={saving}
        className="mt-2 cursor-pointer rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

const AdminRecruitmentPage = () => {
  const [tab, setTab] = useState("all");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchRowsCache, setSearchRowsCache] = useState([]);
  const [loadingSearchRows, setLoadingSearchRows] = useState(false);
  const [positionFilter, setPositionFilter] = useState("");
  const [positionMenuOpen, setPositionMenuOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsCache, setRowsCache] = useState([]);
  const [localOverrides, setLocalOverrides] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const { accessToken } = useSelector((state) => state.adminAuth);

  const statusFilter =
    tab === "shortlisted" || tab === "unsent" || tab === "no-time"
      ? "shortlisted"
      : "all";
  const onlyUnshortlisted = tab === "unshortlisted";
  const inviteStatus = tab === "unsent" ? "unsent" : "all";
  const noInterviewTime = tab === "no-time";

  const { data: pageData, isFetching: loadingPage, refetch: refetchApplications } =
    useGetAdminRecruitmentApplicationsQuery({
      status: statusFilter,
      position: positionFilter,
      unshortlisted: onlyUnshortlisted,
      inviteStatus,
      noInterviewTime,
      page: currentPage,
      limit: 20,
    });
  const { data: positionOptions = [] } = useGetAdminRecruitmentPositionsQuery();
  const { data: statsData, isFetching: loadingStats, refetch: refetchStats } = useGetAdminRecruitmentStatsQuery(
    {
      status: statusFilter,
      position: positionFilter,
      unshortlisted: onlyUnshortlisted,
      inviteStatus,
      noInterviewTime,
    },
    { skip: !statsOpen }
  );

  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(1);
    setTotalItems(0);
    setRowsCache([]);
    setSearchRowsCache([]);
    setLocalOverrides({});
    setPositionMenuOpen(false);
  }, [tab, positionFilter]);

  useEffect(() => {
    if (!pageData) return;
    setRowsCache(Array.isArray(pageData.applications) ? pageData.applications : []);
    setTotalPages(Math.max(1, Number(pageData.totalPages || 1)));
    setTotalItems(Number(pageData.total || 0));
  }, [pageData]);

  const mergedRows = useMemo(() => {
    return rowsCache.map((row) => ({
      ...row,
      ...localOverrides[row.id],
    }));
  }, [rowsCache, localOverrides]);

  const mergedSearchRows = useMemo(() => {
    return searchRowsCache.map((row) => ({
      ...row,
      ...localOverrides[row.id],
    }));
  }, [searchRowsCache, localOverrides]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  useEffect(() => {
    if (!normalizedSearch || !accessToken) {
      setSearchRowsCache([]);
      setLoadingSearchRows(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchAllApplicationsForSearch = async () => {
      try {
        setLoadingSearchRows(true);

        const limit = 100;
        const buildUrl = (page) => {
          const params = new URLSearchParams();
          params.set("status", statusFilter);
          params.set("position", positionFilter);
          params.set("unshortlisted", String(onlyUnshortlisted));
          params.set("inviteStatus", inviteStatus);
          params.set("noInterviewTime", String(noInterviewTime));
          params.set("page", String(page));
          params.set("limit", String(limit));
          return `${API_BASE_URL}/admin/recruitment/applications?${params.toString()}`;
        };

        const firstResponse = await fetch(buildUrl(1), {
          method: "GET",
          credentials: "include",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        });

        if (!firstResponse.ok) {
          throw new Error("Failed to fetch recruitment applications for search");
        }

        const firstPayload = await firstResponse.json();
        const firstRows = Array.isArray(firstPayload?.applications)
          ? firstPayload.applications
          : [];
        const allRows = [...firstRows];
        const allPages = Math.max(1, Number(firstPayload?.totalPages || 1));

        for (let page = 2; page <= allPages; page += 1) {
          const response = await fetch(buildUrl(page), {
            method: "GET",
            credentials: "include",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error("Failed to fetch recruitment applications for search");
          }

          const payload = await response.json();
          if (Array.isArray(payload?.applications)) {
            allRows.push(...payload.applications);
          }
        }

        if (!cancelled) {
          setSearchRowsCache(allRows);
        }
      } catch (error) {
        if (!cancelled && error?.name !== "AbortError") {
          console.error(error);
          setSearchRowsCache([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSearchRows(false);
        }
      }
    };

    fetchAllApplicationsForSearch();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    normalizedSearch,
    accessToken,
    statusFilter,
    positionFilter,
    onlyUnshortlisted,
    inviteStatus,
    noInterviewTime,
  ]);

  const sourceRows = normalizedSearch ? mergedSearchRows : mergedRows;

  const filteredRows = useMemo(() => {
    if (!normalizedSearch) return sourceRows;

    return sourceRows.filter((row) => {
      const searchableText = [
        row.fullName,
        row.email,
        row.studentId,
        row.department,
        row.batch,
        row.phoneNumber,
        row.status,
        row.cgpa,
        row.creditsCompleted,
        row.recruitmentCycle,
        row.currentAddress,
        row.otherClubNames,
        row.enrolledInOtherClub,
        row.github,
        row.linkedin,
        row.problemSolvingExperience,
        row.whyCapable,
        row.remarks,
        row.interviewTime,
        row.interviewEmailSentAt,
        Array.isArray(row.positions) ? row.positions.join(" ") : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [sourceRows, normalizedSearch]);

  const setRowOverride = (id, patch) => {
    // update in api and optimistically update in UI

    setLocalOverrides((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...patch,
      },
    }));
    
  };

  const updateApplicationField = async (id, payload) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/recruitment/applications/${id}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update application");
    }

    return response.json().catch(() => null);
  };

  const handleSaveRemarks = async (id, remarks) => {
    const key = `${id}-remarks`;
    try {
      setSavingMap((prev) => ({ ...prev, [key]: true }));
      setRowOverride(id, { remarks });

      await updateApplicationField(id, { remarks });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveStatus = async (id, status) => {
    const key = `${id}-status`;
    try {
      setSavingMap((prev) => ({ ...prev, [key]: true }));
      setRowOverride(id, { status });

      await updateApplicationField(id, { status });
      await refetchApplications();
      if (statsOpen) {
        await refetchStats();
      }
      setLocalOverrides((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveInterviewTime = async (id, interviewTime) => {
    const key = `${id}-interviewTime`;
    try {
      setSavingMap((prev) => ({ ...prev, [key]: true }));
      setRowOverride(id, { interviewTime });
      await updateApplicationField(id, { interviewTime });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSendInterviewInvitation = async (row) => {
    const key = `${row.id}-sendInvite`;
    try {
      setSavingMap((prev) => ({ ...prev, [key]: true }));

      const response = await fetch(
        `${API_BASE_URL}/admin/recruitment/applications/${row.id}/send-invitation`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send invitation email");
      }

      const payload = await response.json().catch(() => ({}));
      const sentAt = payload?.application?.interviewEmailSentAt || new Date().toISOString();
      setRowOverride(row.id, { interviewEmailSentAt: sentAt });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const openProtectedFile = async (id, type) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/recruitment/applications/${id}/files/${type}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to load file");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const downloadExport = async (status) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/recruitment/export?status=${status}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to export");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `recruitment-${status}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const downloadPhoneExport = async (status) => {
    const response = await fetch(
      `${API_BASE_URL}/admin/recruitment/export-phones?status=${status}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to export phone numbers");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `recruitment-phones-${status}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const downloadParticipantsByTimeExport = async (status = "shortlisted") => {
    const response = await fetch(
      `${API_BASE_URL}/admin/recruitment/export-participants-by-time?status=${status}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to export participants by time");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `recruitment-participants-by-time-${status}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const closeModal = () => setSelectedApplication(null);
  const closeStatsModal = () => setStatsOpen(false);

  return (
    <>
      <AdminSectionCard
        title="Recruitment Applications"
        subtitle="Review, search, update remarks and status, and manage application records."
      >
        <div className="mb-5 space-y-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <TabButton active={tab === "all"} onClick={() => setTab("all")}>
                All Applications
                <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {tab === "all" ? totalItems : 0}
                </span>
              </TabButton>

              <TabButton
                active={tab === "shortlisted"}
                onClick={() => setTab("shortlisted")}
              >
                Shortlisted
                <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {tab === "shortlisted" ? totalItems : 0}
                </span>
              </TabButton>

              <TabButton
                active={tab === "unshortlisted"}
                onClick={() => setTab("unshortlisted")}
              >
                Unshortlisted
                <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {tab === "unshortlisted" ? totalItems : 0}
                </span>
              </TabButton>

              <TabButton
                active={tab === "unsent"}
                onClick={() => setTab("unsent")}
              >
                Unsent Email
                <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {tab === "unsent" ? totalItems : 0}
                </span>
              </TabButton>

              <TabButton
                active={tab === "no-time"}
                onClick={() => setTab("no-time")}
              >
                No Interview Time
                <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {tab === "no-time" ? totalItems : 0}
                </span>
              </TabButton>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <ActionButton
                className="bg-slate-700 text-white hover:bg-slate-800"
                onClick={() => setStatsOpen(true)}
              >
                Statistics
              </ActionButton>

              <ActionButton
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() => downloadExport("all")}
              >
                Export All
              </ActionButton>

              <ActionButton
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() => downloadExport("shortlisted")}
              >
                Export Shortlisted
              </ActionButton>

              <ActionButton
                className="bg-sky-600 text-white hover:bg-sky-700"
                onClick={() => downloadPhoneExport("all")}
              >
                Export Phones
              </ActionButton>

              <ActionButton
                className="bg-violet-600 text-white hover:bg-violet-700"
                onClick={() => downloadParticipantsByTimeExport("shortlisted")}
              >
                Export By Time
              </ActionButton>
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full flex-col gap-2 lg:max-w-2xl lg:flex-row">
              <div className="relative w-full lg:max-w-md">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, ID, department, CGPA, position, remarks..."
                  className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </div>
              <PositionFilterDropdown
                value={positionFilter}
                options={positionOptions}
                open={positionMenuOpen}
                setOpen={setPositionMenuOpen}
                onChange={(position) => setPositionFilter(position)}
                onClear={() => setPositionFilter("")}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="rounded-full bg-slate-100 px-3 py-1 font-medium">
                Showing {filteredRows.length} of {totalItems}
              </span>
              {searchTerm ? (
                <button
                  className="cursor-pointer rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </button>
              ) : null}
              {positionFilter ? (
                <button
                  className="cursor-pointer rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700 transition hover:bg-slate-200"
                  onClick={() => setPositionFilter("")}
                >
                  Clear Position
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {(loadingPage && currentPage === 1 && !rowsCache.length) ||
        (normalizedSearch && loadingSearchRows) ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 rounded-xl border border-slate-100 p-4"
                >
                  <div className="h-11 w-11 animate-pulse rounded-xl bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                    <div className="h-3 w-28 animate-pulse rounded bg-slate-100" />
                  </div>
                  <div className="h-8 w-24 animate-pulse rounded-lg bg-slate-100" />
                </div>
              ))}
            </div>
          </div>
        ) : !filteredRows.length ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
            <p className="text-sm font-medium text-slate-700">No applications found.</p>
            <p className="mt-1 text-xs text-slate-500">
              Try another search keyword or switch tab.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">
              Swipe horizontally inside the table on smaller screens.
            </div>

            <div className="w-full overflow-hidden">
              <div
                className="w-full overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-slate-100"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <table className="min-w-[1310px] table-fixed text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="w-[240px] px-4 py-3 text-left font-semibold">
                        Applicant
                      </th>
                      <th className="w-[150px] px-4 py-3 text-left font-semibold">
                        Academic
                      </th>
                      <th className="w-[170px] px-4 py-3 text-left font-semibold">
                        Positions
                      </th>
                      <th className="w-[140px] px-4 py-3 text-left font-semibold">
                        Links
                      </th>
                      <th className="w-[240px] px-4 py-3 text-left font-semibold">
                        Remarks
                      </th>
                      <th className="w-[170px] px-4 py-3 text-left font-semibold">
                        Status
                      </th>
                      <th className="w-[220px] px-4 py-3 text-left font-semibold">
                        Interview
                      </th>
                      <th className="w-[120px] px-4 py-3 text-left font-semibold">
                        Details
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredRows.map((row) => (
                      <tr
                        key={row.id}
                        className="align-top transition hover:bg-slate-50/80"
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-start gap-3">
                            <PhotoCell
                              rowId={row.id}
                              fullName={row.fullName}
                              accessToken={accessToken}
                            />

                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold text-slate-900">
                                {row.fullName}
                              </p>
                              <a
                                href={`mailto:${row.email}`}
                                className="block truncate text-xs text-blue-600 underline"
                              >
                                {row.email}
                              </a>

                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                                  ID: {row.studentId}
                                </span>
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                                  {row.department || "-"}
                                </span>
                                {row.batch ? (
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700">
                                    Batch {row.batch}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className="space-y-1.5 text-xs text-slate-700">
                            <p>
                              <span className="font-semibold text-slate-900">
                                Credits:
                              </span>{" "}
                              {row.creditsCompleted || "-"}
                            </p>
                            <p>
                              <span className="font-semibold text-slate-900">
                                CGPA:
                              </span>{" "}
                              {row.cgpa || "-"}
                            </p>
                            <p>
                              <span className="font-semibold text-slate-900">
                                Cycle:
                              </span>{" "}
                              {row.recruitmentCycle || "-"}
                            </p>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            {Array.isArray(row.positions) && row.positions.length ? (
                              row.positions.map((position, index) => (
                                <span
                                  key={`${row.id}-${position}-${index}`}
                                  className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 ring-1 ring-blue-100"
                                >
                                  {position}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-slate-400">No positions</span>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex flex-col items-start gap-2">
                            <FileButton onClick={() => openProtectedFile(row.id, "resume")}>
                              <FileIcon />
                              CV
                            </FileButton>

                            <FileButton onClick={() => openProtectedFile(row.id, "photo")}>
                              <FileIcon />
                              Photo
                            </FileButton>

                            {row.workSampleDriveLink ? (
                              <LinkChip href={row.workSampleDriveLink}>
                                Work Samples
                                <ExternalLinkIcon />
                              </LinkChip>
                            ) : (
                              <span className="text-xs text-slate-400">No samples</span>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <RowRemarksEditor
                            value={row.remarks}
                            onChange={(val) => setRowOverride(row.id, { remarks: val })}
                            onSave={(val) => handleSaveRemarks(row.id, val)}
                            saving={savingMap[`${row.id}-remarks`]}
                          />
                        </td>

                        <td className="px-4 py-4">
                          <RowStatusEditor
                            value={row.status}
                            saving={savingMap[`${row.id}-status`]}
                            onSave={(nextStatus) =>
                              handleSaveStatus(row.id, nextStatus)
                            }
                          />
                        </td>

                        <td className="px-4 py-4">
                          <RowInterviewEditor
                            value={row.interviewTime}
                            onChange={(val) => setRowOverride(row.id, { interviewTime: val })}
                            onSave={(val) => handleSaveInterviewTime(row.id, val)}
                            saving={savingMap[`${row.id}-interviewTime`]}
                          />
                          <p className="mt-2 text-[11px] text-slate-600">
                            {formatInterviewSchedule(row.interviewTime)}
                          </p>
                          {row.interviewEmailSentAt ? (
                            <p className="mt-1 text-[11px] font-semibold text-emerald-700">
                              Invitation sent: {formatSentAt(row.interviewEmailSentAt)}
                            </p>
                          ) : (
                            <p className="mt-1 text-[11px] font-semibold text-amber-700">
                              Invitation not sent
                            </p>
                          )}
                          <button
                            onClick={() => handleSendInterviewInvitation(row)}
                            disabled={
                              savingMap[`${row.id}-sendInvite`] ||
                              !String(row.interviewTime || "").trim() ||
                              Boolean(row.interviewEmailSentAt)
                            }
                            className="mt-2 cursor-pointer rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {savingMap[`${row.id}-sendInvite`]
                              ? "Sending..."
                              : row.interviewEmailSentAt
                              ? "Email Sent"
                              : "Send Email"}
                          </button>
                        </td>

                        <td className="px-4 py-4">
                          <ActionButton
                            className="bg-slate-900 text-white hover:bg-slate-800"
                            onClick={() => setSelectedApplication(row)}
                          >
                            View
                          </ActionButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <ActionButton
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  disabled={loadingPage || currentPage <= 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                >
                  Previous
                </ActionButton>
                <ActionButton
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  disabled={loadingPage || currentPage >= totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  Next
                </ActionButton>
              </div>
            </div>
          </div>
        )}
      </AdminSectionCard>

      {selectedApplication ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-4 shadow-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex flex-col gap-4 border-b border-slate-100 pb-5 md:flex-row md:items-start md:justify-between">
              <div className="flex items-center gap-4">
                <PhotoCell
                  rowId={selectedApplication.id}
                  fullName={selectedApplication.fullName}
                  accessToken={accessToken}
                />
                <div className="min-w-0">
                  <h3 className="break-words text-xl font-bold text-slate-900 sm:text-2xl">
                    {selectedApplication.fullName}
                  </h3>
                  <p className="text-sm text-slate-500">
                    Application ID #{selectedApplication.id} •{" "}
                    {selectedApplication.recruitmentCycle || "N/A"}
                  </p>
                </div>
              </div>

              <button
                className="cursor-pointer rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                onClick={closeModal}
              >
                Close
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 min-w-0">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="mb-3 text-base font-bold text-slate-900">
                  Basic Information
                </p>
                <div className="space-y-2">
                  <InfoRow label="Student ID" value={selectedApplication.studentId} />
                  <InfoRow label="Batch" value={selectedApplication.batch} />
                  <InfoRow label="Department" value={selectedApplication.department} />
                  <InfoRow
                    label="Credits Completed"
                    value={selectedApplication.creditsCompleted}
                  />
                  <InfoRow label="CGPA" value={selectedApplication.cgpa} />
                  <InfoRow
                    label="Phone"
                    value={selectedApplication.phoneNumber}
                    href={
                      selectedApplication.phoneNumber
                        ? `tel:${selectedApplication.phoneNumber}`
                        : undefined
                    }
                  />
                  <InfoRow
                    label="Email"
                    value={selectedApplication.email}
                    href={
                      selectedApplication.email
                        ? `mailto:${selectedApplication.email}`
                        : undefined
                    }
                  />
                  <InfoRow
                    label="Address"
                    value={selectedApplication.currentAddress}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="mb-3 text-base font-bold text-slate-900">
                  Application Details
                </p>
                <div className="space-y-2">
                  <InfoRow
                    label="Applied Positions"
                    value={
                      Array.isArray(selectedApplication.positions)
                        ? selectedApplication.positions.join(", ")
                        : "-"
                    }
                  />
                  <InfoRow
                    label="Other Club"
                    value={selectedApplication.enrolledInOtherClub}
                  />
                  <InfoRow
                    label="Club Names"
                    value={selectedApplication.otherClubNames}
                  />
                  <InfoRow
                    label="GitHub"
                    value={selectedApplication.github}
                    href={selectedApplication.github}
                  />
                  <InfoRow
                    label="LinkedIn"
                    value={selectedApplication.linkedin}
                    href={selectedApplication.linkedin}
                  />
                  <InfoRow
                    label="Terms Accepted"
                    value={selectedApplication.agreedToTerms ? "Yes" : "No"}
                  />
                  <InfoRow label="Status" value={formatStatus(selectedApplication.status)} />
                  <InfoRow
                    label="Interview Schedule"
                    value={formatInterviewSchedule(selectedApplication.interviewTime)}
                  />
                  <InfoRow
                    label="Interview Email"
                    value={
                      selectedApplication.interviewEmailSentAt
                        ? `Sent at ${formatSentAt(selectedApplication.interviewEmailSentAt)}`
                        : "Not sent"
                    }
                  />
                </div>
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-base font-bold text-slate-900">
                  Problem Solving Experience
                </p>
                <p className="rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700 break-words">
                  {selectedApplication.problemSolvingExperience || "-"}
                </p>
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-base font-bold text-slate-900">
                  Why Capable for Selected Role(s)
                </p>
                <p className="rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700 break-words">
                  {selectedApplication.whyCapable || "-"}
                </p>
              </div>

              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-base font-bold text-slate-900">Remarks</p>
                <p className="rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700 break-words">
                  {selectedApplication.remarks || "No remarks added yet."}
                </p>
              </div>

              <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
                <ActionButton
                  className="bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => openProtectedFile(selectedApplication.id, "resume")}
                >
                  View CV
                </ActionButton>

                <ActionButton
                  className="bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={() => openProtectedFile(selectedApplication.id, "photo")}
                >
                  View Photo
                </ActionButton>

                {selectedApplication.workSampleDriveLink ? (
                  <a
                    href={selectedApplication.workSampleDriveLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex cursor-pointer items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Open Work Samples
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {statsOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4 backdrop-blur-sm"
          onClick={closeStatsModal}
        >
          <div
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-slate-100 p-4 shadow-2xl sm:max-w-5xl sm:rounded-3xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 sm:text-xl">
                  Application Statistics
                </h3>
                <p className="text-xs text-slate-500 sm:text-sm">
                  Total applications: {statsData?.totalApplications || 0}
                </p>
              </div>

              <button
                className="cursor-pointer rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50"
                onClick={closeStatsModal}
              >
                Close
              </button>
            </div>

            {loadingStats && !statsData ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="h-56 animate-pulse rounded-2xl border border-slate-200 bg-white"
                  />
                ))}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <StatsListCard
                  title="By Post"
                  items={statsData?.byPost || []}
                  emptyText="No applications found for posts."
                />
                <StatsListCard
                  title="By Department"
                  items={statsData?.byDepartment || []}
                  emptyText="No department data found."
                />
                <StatsListCard
                  title="By Batch"
                  items={statsData?.byBatch || []}
                  emptyText="No batch data found."
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AdminRecruitmentPage;
